import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cccdAdmin, type AdminTeamListItem } from '@/lib/api/cccdAdminClient';
import { TeamStatusBadge } from '@/components/cccd/TeamStatusBadge';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import DataTable, { type DataTableColumn } from '@/components/ui/table/DataTable';
import { EyeIcon } from '@/icons';
import { LoadState } from "@/components/ui/LoadState"

const STAT_GROUPS = [
  {
    heading: 'Requieren acción',
    hint: 'Sin esto, el equipo se queda atascado y puede perder el cupo',
    tone: 'warning' as const,
    statuses: ['negotiating', 'payment_pending'] as const,
  },
  {
    heading: 'En curso',
    hint: 'Avanzando por su cuenta, sin bloqueos',
    tone: 'neutral' as const,
    statuses: ['draft'] as const,
  },
  {
    heading: 'Confirmados',
    hint: 'Listos para el evento',
    tone: 'good' as const,
    statuses: ['active'] as const,
  },
  {
    heading: 'Cerrados',
    hint: 'Ya no requieren seguimiento',
    tone: 'neutral' as const,
    statuses: ['inactive', 'rejected'] as const,
  },
];

export default function CCCDDashboardPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['teams', { limit: 200 }],
    queryFn: () => cccdAdmin.listTeams({ limit: 200 }),
  });

  const { data: institutionsSummary } = useQuery({
    queryKey: ['cccd-institutions-summary'],
    queryFn: () => cccdAdmin.getInstitutionsSummary(),
  });

  const [eventFilter, setEventFilter] = useState('');

  const allTeams: AdminTeamListItem[] = data?.teams ?? [];

  const countByStatus = (status: string) => allTeams.filter((t) => t.status === status).length;
  const actionRequiredAll = allTeams.filter(
    (t) => t.status === 'negotiating' || (t.status === 'payment_pending' && t._count.payments > 0)
  );
  const eventOptions = useMemo(() => {
    const names = new Set(actionRequiredAll.map((t) => t.event?.name ?? t.regionalEvent?.name).filter((n): n is string => !!n));
    return Array.from(names);
  }, [actionRequiredAll]);
  const actionRequired = eventFilter
    ? actionRequiredAll.filter((t) => (t.event?.name ?? t.regionalEvent?.name) === eventFilter)
    : actionRequiredAll;

  const columns: DataTableColumn<AdminTeamListItem>[] = [
    {
      key: 'name',
      label: 'Equipo',
      render: (team) => <span className="font-medium text-gray-900 dark:text-white">{team.name}</span>,
    },
    {
      key: 'event',
      label: 'Evento',
      render: (team) => (
        <span className="text-gray-600 dark:text-gray-400">{team.event?.name ?? team.regionalEvent?.name ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (team) => <TeamStatusBadge status={team.status} />,
    },
    {
      key: 'createdAt',
      label: 'Desde',
      render: (team) => (
        <span className="text-xs text-gray-500">{new Date(team.createdAt).toLocaleDateString('es-CO')}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (team) => (
        <div className="flex justify-end">
          <RowActionsMenu actions={[{ label: 'Ver equipo', icon: <EyeIcon />, to: `/cccd/teams/${team.id}` }]} />
        </div>
      ),
    },
  ];

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, 'CCCD', 'Panel']}
      title="CCCD"
      subtitle="Resumen de equipos y concursos, organizado por lo que requiere tu atención"
      tabs={[
        { label: 'Panel', to: '/cccd', active: true },
        { label: 'Nacionales', to: '/cccd/events' },
        { label: 'Regionales', to: '/cccd/regionals' },
        { label: 'Estadísticas de embudo', to: '/cccd/funnel-stats' },
      ]}
      metrics={[]}
      toolbar={{
        filters: [
          {
            type: 'select',
            key: 'event',
            label: 'Evento',
            value: eventFilter,
            onChange: setEventFilter,
            options: [{ label: 'Todos los eventos', value: '' }, ...eventOptions.map((n) => ({ label: n, value: n }))],
          },
        ],
        onRefresh: () => refetch(),
        refreshing: isLoading,
      }}
    >
      {/* Stat cards — agrupadas por lo que significan para la toma de decisiones, no por el enum crudo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5" aria-busy={isLoading}>
        {STAT_GROUPS.map((group) => {
          const total = group.statuses.reduce((sum, status) => sum + countByStatus(status), 0);
          const valueTone =
            group.tone === 'warning' && total > 0
              ? 'text-warning-600 dark:text-orange-400'
              : group.tone === 'good'
                ? 'text-success-600 dark:text-success-500'
                : 'text-gray-900 dark:text-white';
          return (
            <div
              key={group.heading}
              className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {group.heading}
              </p>
              <p className={`mt-1 text-3xl font-bold ${valueTone}`}>
                {isLoading ? (
                  <span className="inline-block h-8 w-10 animate-pulse rounded bg-gray-100 dark:bg-gray-800" aria-hidden="true" />
                ) : (
                  total
                )}
              </p>
              <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400">{group.hint}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {group.statuses.map((status) => (
                  // Ya no enlaza a /cccd/teams: ese conteo suma equipos nacionales y
                  // regionales, pero /cccd/teams solo lista nacionales desde el split
                  // por ámbito (openspec/changes/cccd-teams-split-by-scope).
                  <span
                    key={status}
                    className="flex items-center gap-1 rounded-full border border-gray-200 px-2 py-0.5 dark:border-gray-700"
                  >
                    <TeamStatusBadge status={status as any} />
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {isLoading ? "…" : countByStatus(status)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Instituciones representadas */}
      {institutionsSummary && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Instituciones representadas
          </p>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">{institutionsSummary.total}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">{institutionsSummary.public}</span> públicas
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">{institutionsSummary.private}</span> privadas
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">{institutionsSummary.unclassified}</span> sin clasificar
            </span>
          </div>
          <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
            Cuenta cada institución una sola vez, aunque tenga varios equipos inscritos
          </p>
        </div>
      )}

      {/* Needs action */}
      <div>
        <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Requieren acción</h2>
        {isLoading ? (
          <LoadState />
        ) : actionRequired.length === 0 ? (
          <p role="status" aria-live="polite" className="text-sm text-gray-500 dark:text-gray-400">Todo al día.</p>
        ) : (
          <DataTable
            columns={columns}
            rows={actionRequired}
            rowKey={(team) => team.id}
            page={1}
            pageSize={Math.max(actionRequired.length, 1)}
            total={actionRequired.length}
            onPageChange={() => {}}
          />
        )}
      </div>
    </ModuleLayout>
  );
}
